import React, { useState, useEffect, useMemo, useRef } from "react";
import "./HoneycombBanner.css";
import { HONEYCOMB_IMAGES } from "./data/content";

/*
 * Animation and layout constants.
 * Tweak these to change the feel of the banner without touching logic.
 * Full walkthrough: docs/HONEYCOMB-BANNER.md
 */
const ROW_GAP_MS = 65;           // delay between each row starting its cascade
const ROW_SPREAD_MS = 150;       // random spread window for hexes within a row
const CASCADE_DURATION_MS = 550; // length of the hexCascadeIn CSS animation
const IMAGE_DISPLAY_MS = 7000;  // how long a logo stays visible before flipping closed
const FLIP_ANIM_MS = 1700;       // duration of the Y-axis flip (passed to CSS)
const OPEN_STAGGER_MS = 220;     // pause after image is loaded before flip starts
const PAINT_BEFORE_FLIP_MS = 120; // extra buffer so the back face paints before flip
const PAUSE_BEFORE_NEXT_MS = 1500; // wait after cascade before the first logo flip
const HEX_HEIGHT_RATIO = 1.1547; // hex cells are ~15% taller than wide so the shape isn't squashed
const ROW_OVERLAP_RATIO = 0.24;  // how much rows overlap vertically (honeycomb nest)
const RESIZE_DEBOUNCE_MS = 120;  // delay before rebuilding grid on window resize
const RECENT_CLOSED_LIMIT = 3;   // avoid re-picking the last N hexes that just closed

/**
 * Returns the current browser viewport size.
 * Falls back to a desktop-ish default when `window` isn't available (SSR / tests).
 */
function getWindowSize() {
  if (typeof window === "undefined") {
    return { width: 1200, height: 800 };
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Maps viewport width to a column count.
 * More columns on wider screens = smaller hexes and a denser grid.
 */
function getTargetCols(width) {
  if (width < 480) return 4;
  if (width < 600) return 4;
  if (width < 768) return 4;
  if (width < 962) return 5;
  if (width < 1200) return 6;
  if (width < 1440) return 8;
  return 10;
}

/**
 * Resolves an image entry to a URL string.
 * Webpack/bundler imports come through as `{ default: "url" }`; plain strings pass through.
 */
function resolveImageSrc(image) {
  if (typeof image === "string") return image;
  if (image && image.default) return image.default;
  return "";
}

/**
 * Normalizes entries from content.js into a consistent shape for rendering.
 * Supports plain imports (treated as logos) or `{ src, variant }` objects
 * where variant is "logo" (padded contain) or "photo" (cover crop).
 */
function normalizeBannerImage(entry) {
  if (entry && typeof entry === "object" && entry.src) {
    return {
      src: resolveImageSrc(entry.src),
      variant: entry.variant || "logo",
    };
  }

  return {
    src: resolveImageSrc(entry),
    variant: "logo",
  };
}

/**
 * Waits until the logo image is actually visible on screen before starting the flip.
 * Without this pause, the hex can flip over while still empty and flash blank for a moment.
 */
function waitForPaint(callback) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setTimeout(callback, PAINT_BEFORE_FLIP_MS);
    });
  });
}

/** Returns a random cell id from the given pool, or null if the pool is empty. */
function pickRandomCellId(cells) {
  if (!cells.length) return null;
  return cells[Math.floor(Math.random() * cells.length)].id;
}

/**
 * Returns cells allowed to flip — middle rows only.
 * Top and bottom rows are excluded so logos near the fade mask don't look clipped.
 */
function getFlipEligibleCells(cells, totalRows) {
  if (totalRows <= 2) return [];
  return cells.filter((cell) => cell.row > 0 && cell.row < totalRows - 1); // filter out top and bottom rows
}

/**
 * Picks a random flip target from eligible middle-row hexes.
 * This is separate from logo order (shuffleArray) — we only randomize WHICH tile
 * flips; WHAT logo shows comes from the shuffled deck in takeNextImage.
 *
 * Tries to honour `excludeIds` (recently used hexes) but relaxes the list
 * if no cells remain — so we always flip somewhere rather than stalling.
 */
function pickRandomFlipCellId(cells, totalRows, excludeIds = []) {
  const eligible = getFlipEligibleCells(cells, totalRows); // get all eligible cells
  const uniqueExclude = [...new Set(excludeIds.filter((id) => id != null))]; // remove null values from excludeIds

  for (let limit = uniqueExclude.length; limit >= 0; limit -= 1) {
    const blocked = new Set(uniqueExclude.slice(0, limit)); // create a set of blocked cells
    const pool = eligible.filter((cell) => !blocked.has(cell.id)); // filter out blocked cells from eligible cells
    if (pool.length) {
      return pickRandomCellId(pool); // pick a random cell from the pool
    }
  }

  return null;
}

/**
 * Fisher–Yates shuffle — returns a new array in random order.
 
 * Walk backwards through the list of logo images and swap each slot with a random earlier slot.
 * Every possible order is equally likely — better than sorting by Math.random().
 */
function shuffleArray(items) {
  const copy = [...items]; // create a copy of the array to keep the original array intact
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1)); // generate a random index to swap with (between 0 and i)
    [copy[i], copy[j]] = [copy[j], copy[i]]; // swap the elements
  }
  return copy;
}

/**
 * Compares two banner entries (import or `{ src }` object) for equality.
 * Used to prevent the same logo appearing twice in a row across deck reshuffles.
 */
function isSameBannerEntry(a, b) {
  if (a === b) return true;
  if (a && b && a.src && b.src) return a.src === b.src;
  return false;
}

/**
 * Builds a fresh shuffled list of all logos.

 * If `avoidImmediate` is set, swaps the first card so it doesn't match the
 * logo that was just shown — so a reshuffle doesn't start with the same logo twice.
 */
function createShuffledDeck(images, avoidImmediate = null) {
  if (images.length === 0) return [];
  if (images.length === 1) return [...images];

  let deck = shuffleArray(images);
  if (avoidImmediate && isSameBannerEntry(deck[0], avoidImmediate)) {
    const swapIndex = deck.findIndex((img) => !isSameBannerEntry(img, avoidImmediate));
    [deck[0], deck[swapIndex]] = [deck[swapIndex], deck[0]];
  }
  return deck;
}

/**
 * Returns the next logo from the shuffled deck (mutates deckRef).
 * When the deck is empty, creates a new shuffle — every logo is shown once
 * per cycle before any repeats.
 */
function takeNextImage(deckRef, images, lastShownRef) {
  if (images.length === 0) return null;

  if (deckRef.current.length === 0) {
    deckRef.current = createShuffledDeck(images, lastShownRef.current);
  }

  const next = deckRef.current.shift();
  lastShownRef.current = next;
  return next;
}

/**
 * Honeycomb rows alternate between `baseCols` and `baseCols + 1` hexes.
 * Odd rows get the extra hex so the staggered offset rows fill the width.
 */
function getRowCols(rowIndex, baseCols) {
  return rowIndex % 2 === 1 ? baseCols + 1 : baseCols;
}

/**
 * Assigns each cell a unique cascade delay for the entrance animation.
 * Each row starts ROW_GAP_MS after the previous; within a row, delays are
 * randomized and reassigned so the wave feels organic rather than left-to-right.
 */
function buildCascadeDelays(rows, baseCols) {
  const delays = {};
  const rowWindow = ROW_GAP_MS + ROW_SPREAD_MS;
  let id = 0;

  for (let row = 0; row < rows; row += 1) {
    const rowStart = row * rowWindow;
    const rowCols = getRowCols(row, baseCols);
    const colOffsets = [];

    for (let col = 0; col < rowCols; col += 1) {
      colOffsets.push(Math.random() * ROW_SPREAD_MS);
    }

    colOffsets.sort(() => Math.random() - 0.5);

    for (let col = 0; col < rowCols; col += 1) {
      delays[id] = rowStart + colOffsets[col];
      id += 1;
    }
  }

  return delays;
}

/**
 * Returns the delay of the last hex to start cascading.
 * Used to schedule `cascadeComplete` after the final hex finishes animating.
 */
function getMaxCascadeDelay(rows) {
  const rowWindow = ROW_GAP_MS + ROW_SPREAD_MS;
  return (rows - 1) * rowWindow + ROW_SPREAD_MS;
}

/**
 * Creates the initial state array for every hex in the grid.
 * Each cell tracks its position, flip state, current image, and cascade timing.
 */
function buildGrid(rows, baseCols) {
  const delays = buildCascadeDelays(rows, baseCols);
  const cells = [];
  let id = 0;

  for (let row = 0; row < rows; row += 1) {
    const rowCols = getRowCols(row, baseCols);
    for (let col = 0; col < rowCols; col += 1) {
      cells.push({
        id,
        row,
        col,
        image: null,
        imageVariant: null,
        isFlipped: false,
        cascadeDelay: delays[id],
      });
      id += 1;
    }
  }

  return cells;
}

/**
 * Calculates how many rows/columns fit the viewport and the pixel size of each hex.
 * Hex width = viewport width ÷ column count; height follows hex geometry ratio.
 * Rows are computed from banner height with vertical overlap so hexes nest together.
 */
function computeLayout(viewportWidth, viewportHeight) {
  const width = Math.max(viewportWidth, 320);
  const height = Math.max(viewportHeight * 0.58, 280);

  const cols = getTargetCols(width);
  const hexWidth = width / cols;
  const hexHeight = hexWidth * HEX_HEIGHT_RATIO;
  const rowStep = hexHeight * (1 - ROW_OVERLAP_RATIO);
  const rows = Math.max(4, Math.ceil(height / rowStep) + 1);

  return {
    rows,
    cols,
    hexWidth: Math.round(hexWidth * 100) / 100,
    hexHeight: Math.round(hexHeight * 100) / 100,
  };
}

/**
 * Animated honeycomb hero grid.
 * Phase 1: hexes cascade in row by row.
 * Phase 2: one hex at a time flips to reveal a stack logo, then hands off to the next.
 */
function HoneycombBanner({ images = HONEYCOMB_IMAGES }) {
  const initialSize = getWindowSize();
  const stableHeightRef = useRef(initialSize.height);
  const isInViewRef = useRef(true);
  const flipLoopStartedRef = useRef(false);

  const [layout] = useState(() =>
    computeLayout(initialSize.width, stableHeightRef.current)
  );
  const [cells, setCells] = useState(() =>
    buildGrid(layout.rows, layout.cols)
  );
  const [hexScale, setHexScale] = useState({
    width: layout.hexWidth,
    height: layout.hexHeight,
  });
  const [cascadeStarted, setCascadeStarted] = useState(false);
  const [cascadeComplete, setCascadeComplete] = useState(false);

  // Flip-cycle refs — survive renders without triggering re-renders themselves.
  const lastImageRef = useRef(null);
  const imageDeckRef = useRef([]);
  const recentClosedRef = useRef([]);

  const { rows } = layout;
  const totalCells = cells.length;

  const bannerRef = useRef(null);

  // Always points at latest cells — timers read this to avoid stale closures.
  const cellsRef = useRef(cells);
  cellsRef.current = cells;

  /* Track visibility for pausing flips — ref only, no grid rebuild. */
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isInViewRef.current = entry.isIntersecting;
      },
      { threshold: 0.05, rootMargin: "0px 0px 5% 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /*
   * Width-only resize — updates hex size without rebuilding the grid.
   * Ignores mobile address-bar height changes that were replaying the cascade.
   */
  useEffect(() => {
    let timeoutId = null;
    let lastWidth = initialSize.width;

    const onResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const next = getWindowSize();
        if (Math.abs(next.width - lastWidth) < 24) return;
        lastWidth = next.width;
        const nextLayout = computeLayout(next.width, stableHeightRef.current);
        setHexScale({
          width: nextLayout.hexWidth,
          height: nextLayout.hexHeight,
        });
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [initialSize.width]);

  /* Cascade runs once on initial mount only. */
  useEffect(() => {
    const startTimer = setTimeout(() => setCascadeStarted(true), 80);
    const completeTimer = setTimeout(
      () => setCascadeComplete(true),
      getMaxCascadeDelay(rows) + CASCADE_DURATION_MS + 120
    );

    return () => {
      clearTimeout(startTimer);
      clearTimeout(completeTimer);
    };
  }, [rows]);

  /*
   * Logo flip loop — starts once after cascade. Pauses while off-screen via isInViewRef.
   */
  useEffect(() => {
    if (!cascadeComplete || flipLoopStartedRef.current || images.length === 0 || totalCells === 0) {
      return undefined;
    }

    flipLoopStartedRef.current = true;

    if (getFlipEligibleCells(cellsRef.current, rows).length === 0) {
      return undefined;
    }

    let flipBackTimer = null;
    let clearImageTimer = null;
    let openStaggerTimer = null;
    let startTimer = null;
    let visibilityWaitTimer = null;
    let cancelled = false;

    const runWhenVisible = (callback, delayMs) => {
      const tick = () => {
        if (cancelled) return;
        if (!isInViewRef.current) {
          visibilityWaitTimer = setTimeout(tick, 200);
          return;
        }
        callback();
      };
      return setTimeout(tick, delayMs);
    };

    /**
     * Chooses the next hex to flip.
     * Excludes the hex that's closing now plus the last RECENT_CLOSED_LIMIT picks.
     */
    const pickNextCellId = (cells, closingId) => {
      const excludeIds = [closingId, ...recentClosedRef.current].slice(
        0,
        RECENT_CLOSED_LIMIT
      );
      return pickRandomFlipCellId(cells, rows, excludeIds);
    };

    /** Remembers a closed hex so pickNextCellId won't choose it again right away. */
    const recordClosedCell = (closingId) => {
      recentClosedRef.current = [closingId, ...recentClosedRef.current].slice(
        0,
        RECENT_CLOSED_LIMIT
      );
    };

    /**
     * Loads the logo in a hidden Image(), waits for decode + paint,
     * then calls onOpen so the flip animation shows a ready image — not a flash of empty.
     */
    const preloadAndFlipOpen = (pickId, imageSrc, onOpen) => {
      const preload = new window.Image();
      preload.onload = () => {
        if (cancelled) return;

        const open = () => {
          if (cancelled) return;
          onOpen();
        };

        if (preload.decode) {
          preload.decode().then(() => waitForPaint(open)).catch(() => waitForPaint(open));
        } else {
          waitForPaint(open);
        }
      };
      preload.onerror = () => {
        if (cancelled) return;
      };
      preload.src = imageSrc;
    };

    /**
     * Schedules the transition from the current open hex to the next one.
     * After IMAGE_DISPLAY_MS: flip current closed, assign next image to the new hex,
     * preload it, flip the new hex open, and recurse via scheduleHandoff.
     */
    const scheduleHandoff = (closingId) => {
      flipBackTimer = runWhenVisible(() => {
        if (cancelled) return;

        const prev = cellsRef.current;
        const pickId = pickNextCellId(prev, closingId);
        if (pickId === null) return;

        recordClosedCell(closingId);

        const nextImage = takeNextImage(imageDeckRef, images, lastImageRef);
        if (!nextImage) return;
        const { src: imageSrc, variant: imageVariant } =
          normalizeBannerImage(nextImage);
        if (!imageSrc) return;

        setCells(
          prev.map((cell) => {
            if (cell.id === closingId) {
              return { ...cell, isFlipped: false };
            }
            if (cell.id === pickId) {
              return {
                ...cell,
                isFlipped: false,
                image: imageSrc,
                imageVariant,
              };
            }
            return { ...cell, isFlipped: false, image: null, imageVariant: null };
          })
        );

        clearImageTimer = setTimeout(() => {
          if (cancelled) return;
          setCells((current) =>
            current.map((cell) =>
              cell.id === closingId
                ? { ...cell, image: null, imageVariant: null }
                : cell
            )
          );
        }, FLIP_ANIM_MS);

        preloadAndFlipOpen(pickId, imageSrc, () => {
          openStaggerTimer = runWhenVisible(() => {
            if (cancelled) return;

            setCells((current) =>
              current.map((cell) => ({
                ...cell,
                isFlipped: cell.id === pickId,
              }))
            );

            scheduleHandoff(pickId);
          }, OPEN_STAGGER_MS);
        });
      }, IMAGE_DISPLAY_MS);
    };

    /**
     * Starts the flip cycle after a short pause post-cascade.
     * Picks a random middle hex, loads the first logo, and begins the handoff chain.
     */
    const openFirstHex = () => {
      if (cancelled) return;

      const prev = cellsRef.current;
      const pickId = pickRandomFlipCellId(prev, rows);
      if (pickId === null) return;

      const nextImage = takeNextImage(imageDeckRef, images, lastImageRef);
      if (!nextImage) return;
      const { src: imageSrc, variant: imageVariant } =
        normalizeBannerImage(nextImage);
      if (!imageSrc) return;

      setCells(
        prev.map((cell) => ({
          ...cell,
          isFlipped: false,
          image: cell.id === pickId ? imageSrc : null,
          imageVariant: cell.id === pickId ? imageVariant : null,
        }))
      );

      preloadAndFlipOpen(pickId, imageSrc, () => {
        setCells((current) =>
          current.map((cell) => ({
            ...cell,
            isFlipped: cell.id === pickId,
          }))
        );
        scheduleHandoff(pickId);
      });
    };

    startTimer = runWhenVisible(openFirstHex, PAUSE_BEFORE_NEXT_MS);

    return () => {
      cancelled = true;
      flipLoopStartedRef.current = false;
      if (flipBackTimer) clearTimeout(flipBackTimer);
      if (clearImageTimer) clearTimeout(clearImageTimer);
      if (openStaggerTimer) clearTimeout(openStaggerTimer);
      if (startTimer) clearTimeout(startTimer);
      if (visibilityWaitTimer) clearTimeout(visibilityWaitTimer);
    };
  }, [cascadeComplete, images, totalCells, rows]);

  /** Flat cell list → array of rows for rendering staggered hex-row divs. */
  const hexRows = useMemo(() => {
    const grouped = [];
    for (let row = 0; row < rows; row += 1) {
      grouped.push(cells.filter((cell) => cell.row === row));
    }
    return grouped;
  }, [cells, rows]);

  return (
    <div
      ref={bannerRef}
      className="honeycomb-banner"
      style={{
        "--hex-width": `${hexScale.width}px`,
        "--hex-height": `${hexScale.height}px`,
        "--flip-duration": `${FLIP_ANIM_MS}ms`,
      }}
      aria-hidden="true"
    >
      <div className="honeycomb-grid">
        {hexRows.map((rowCells) => {
          const rowIndex = rowCells[0]?.row ?? 0;
          return (
          <div
            className={`hex-row${rowIndex % 2 === 1 ? " hex-row--offset" : ""}`}
            key={`row-${rowIndex}`}
            style={{ "--row-cols": rowCells.length }}
          >
            {rowCells.map((cell) => (
              <div
                className="hex-cell"
                key={cell.id}
              >
                {/* cascade-in: hex drops upright from flat; delay from buildCascadeDelays */}
                <div
                  className={[
                    "hex-inner",
                    cascadeStarted ? "cascade-in" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{ "--delay": `${cell.cascadeDelay}ms` }}
                >
                  {/* show-image: CSS rotates flipper 180° to reveal hex-back */}
                  <div
                    className={[
                      "hex-flipper",
                      cell.isFlipped ? "show-image" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div className="hex-face hex-front">
                      <div className="hex-clip hex-honey" />
                    </div>
                    <div className="hex-face hex-back">
                      <div className="hex-clip">
                        {cell.image ? (
                          <img
                            src={cell.image}
                            alt=""
                            className={
                              "hex-back-image" +
                              (cell.imageVariant === "photo"
                                ? " hex-back-image--photo"
                                : "")
                            }
                            decoding="sync"
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          );
        })}
      </div>
    </div>
  );
}

export default HoneycombBanner;
